import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import type React from "react"
import { useMemo, useState } from "react"
import { FiDownload } from "react-icons/fi"
import { z } from "zod"

import { StudentActionsMenu } from "@/components/layout/Common/StudentActionsMenu"
import { StudentCard } from "@/components/molecules/StudentCard"
import { SwimEmptyState } from "@/components/molecules/SwimEmptyState"
import { type ViewMode, ViewToggle } from "@/components/molecules/ViewToggle"
import {
  DataTable,
  type DataTableColumn,
} from "@/components/organisms/DataTable"
import { AddStudent } from "@/features/students"
import { usePermissions } from "@/hooks/usePermissions"
import { type ExportColumn, exportToExcel } from "@/utils/exportToExcel"
import { useIsMobile } from "@/hooks/useResponsive"
import { api } from "@/services/api"
import type { StudentListResponse, StudentResponse } from "@/types/student"
import { unwrapStandardResponse } from "@/utils/apiResponse"

const studentsSearchSchema = z.object({
  page: z.coerce.number().min(1).catch(1),
  search: z.string().optional(),
  sort: z.enum(["created_at", "updated_at"]).catch("created_at"),
  direction: z.enum(["asc", "desc"]).catch("desc"),
})

const PER_PAGE = 25
type SortField = "created_at" | "updated_at"
type SortDirection = "asc" | "desc"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

function getStudentsQueryOptions({
  page,
  search,
  sort,
  direction,
}: {
  page: number
  search?: string
  sort: SortField
  direction: SortDirection
}) {
  const debugStudents =
    import.meta.env.DEV && import.meta.env.VITE_DEBUG_STUDENTS === "true"

  return {
    queryFn: async () => {
      if (debugStudents) {
        console.log(
          `Students page: Fetching page ${page} with ${PER_PAGE} records per page`,
        )
      }

      const response = await api.students.readStudentsApiV1StudentsGet({
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
        search,
        sortBy: sort,
        sortOrder: direction,
      })

      const payload = unwrapStandardResponse<StudentListResponse>(response)
      const items = payload?.items ?? []
      const total = payload?.total ?? 0

      if (debugStudents) {
        console.log("Students page: API payload:", payload)
        console.log("🔍 Students page: items count:", items.length)
        console.log("🔍 Students page: total:", total)
      }

      return {
        data: items,
        count: total,
      }
    },
    queryKey: [
      "students",
      {
        page,
        search,
        sort,
        direction,
      },
    ],
  }
}

export const Route = createFileRoute("/_layout/students")({
  component: Students,
  validateSearch: (search) => studentsSearchSchema.parse(search),
})

function Students() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { page, search, sort, direction } = Route.useSearch()
  const { hasPermission } = usePermissions()
  const [searchInput, setSearchInput] = useState(search ?? "")
  // Default to cards for parents, table for staff
  const isParentView =
    hasPermission("students:read_own") && !hasPermission("students:read")
  const [viewMode, setViewMode] = useState<ViewMode>(
    isParentView ? "cards" : "table",
  )
  const isMobile = useIsMobile()
  const effectiveViewMode = isMobile ? "cards" : viewMode

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getStudentsQueryOptions({
      page,
      search: search || undefined,
      sort,
      direction,
    }),
    placeholderData: (previous) => previous,
  })

  const setPage = (nextPage: number) =>
    navigate({
      search: (previous) => ({
        ...previous,
        page: nextPage,
      }),
    })

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextValue = searchInput.trim()
    navigate({
      search: (previous) => {
        const { search: _prevSearch, ...rest } = previous
        return nextValue
          ? { ...rest, page: 1, search: nextValue }
          : { ...rest, page: 1 }
      },
    })
  }

  const handleSortChange = (nextSort: SortField) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        sort: nextSort,
      }),
    })
  }

  const handleDirectionChange = (nextDirection: SortDirection) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        direction: nextDirection,
      }),
    })
  }

  const records = data?.data?.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0
  const showActions =
    hasPermission("students:update") || hasPermission("students:delete")

  const columns = useMemo<DataTableColumn<StudentResponse>[]>(() => {
    const tableColumns: DataTableColumn<StudentResponse>[] = []

    // User-friendly columns showing names instead of IDs
    tableColumns.push({
      key: "full_name",
      label: "Student Name",
      render: (student) => (
        <Text fontWeight="medium">
          {student.first_name} {student.last_name}
        </Text>
      ),
    })

    tableColumns.push({
      key: "household_name",
      label: "Parent/Guardian",
      render: (student) => (
        <Text>{student.household_name || "N/A"}</Text>
      ),
    })

    tableColumns.push({
      key: "date_of_birth",
      label: "Date of Birth",
      render: (student) => (
        <Text>{formatFieldValue(student.date_of_birth, "date")}</Text>
      ),
    })

    tableColumns.push({
      key: "gender",
      label: "Gender",
      render: (student) => (
        <Text>{formatFieldValue(student.gender, "str")}</Text>
      ),
    })

    tableColumns.push({
      key: "current_swim_level",
      label: "Swim Level",
      render: (student) => (
        <Text>{formatFieldValue(student.current_swim_level, "str")}</Text>
      ),
    })

    tableColumns.push({
      key: "is_active",
      label: "Status",
      render: (student) => {
        const isActive = student.is_active !== false
        return (
          <Box
            as="span"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
            fontWeight="medium"
            bg={isActive ? "green.50" : "gray.50"}
            color={isActive ? "green.700" : "gray.700"}
          >
            {isActive ? "Active" : "Inactive"}
          </Box>
        )
      },
    })

    if (showActions) {
      tableColumns.push({
        key: "actions",
        label: "Actions",
        render: (student) => <StudentActionsMenu student={student} />,
      })
    }

    return tableColumns
  }, [showActions])

  const studentExportColumns: ExportColumn<StudentResponse>[] = [
    { header: "Name", accessor: (s) => `${s.first_name} ${s.last_name}` },
    { header: "Date of Birth", accessor: (s) => s.date_of_birth },
    { header: "Gender", accessor: (s) => s.gender },
    { header: "Swim Level", accessor: (s) => s.current_swim_level },
    { header: "Household", accessor: (s) => s.household_name },
    { header: "Location", accessor: (s) => s.preferred_location },
    { header: "Active", accessor: (s) => (s.is_active !== false ? "Yes" : "No") },
    { header: "Notes", accessor: (s) => s.notes },
  ]

  const handleExport = () => {
    const today = new Date().toISOString().slice(0, 10)
    exportToExcel(records, studentExportColumns, `students-${today}`, "Students")
  }

  if (isLoading) {
    return (
      <Container maxW="full">
        <Flex justifyContent="space-between" alignItems="center" pt={12} mb={6}>
          <Heading size="lg">Swimmers</Heading>
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="center"
          direction="column"
          gap={4}
          minH="240px"
        >
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.600">Loading swimmers...</Text>
        </Flex>
      </Container>
    )
  }

  return (
    <Container maxW="full">
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        pt={12}
        mb={6}
        gap={{ base: 3, md: 0 }}
      >
        <Flex justify="space-between" align="center">
          <Heading size="lg">Swimmers</Heading>
          <Box display={{ base: "block", md: "none" }}>
            <AddStudent />
          </Box>
        </Flex>
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={{ base: 3, md: 4 }}
          align={{ base: "stretch", md: "center" }}
        >
          <form onSubmit={handleSearchSubmit}>
            <HStack gap={2}>
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search swimmers"
                size="sm"
                width={{ base: "full", md: "220px" }}
                aria-label="Search swimmers"
              />
              <Button type="submit" size="sm" colorPalette="blue">
                Search
              </Button>
            </HStack>
          </form>
          <HStack gap={2}>
            <Box
              {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
              as="select"
              size="sm"
              value={sort}
              onChange={(event) =>
                handleSortChange(
                  (event.target as HTMLSelectElement).value as SortField,
                )
              }
              aria-label="Sort swimmers"
              width={{ base: "full", md: "170px" }}
              flex={{ base: 1, md: "none" }}
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.200"
              px={3}
              py={1}
              fontSize="sm"
              bg="white"
              _hover={{ borderColor: "gray.300" }}
              _focus={{
                borderColor: "blue.500",
                outline: "none",
                boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
              }}
            >
              <option value="created_at">Newest</option>
              <option value="updated_at">Recently updated</option>
              <option value="name">Name</option>
            </Box>
            <Box
              {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
              as="select"
              size="sm"
              value={direction}
              onChange={(event) =>
                handleDirectionChange(
                  (event.target as HTMLSelectElement).value as SortDirection,
                )
              }
              aria-label="Sort direction"
              width={{ base: "full", md: "150px" }}
              flex={{ base: 1, md: "none" }}
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.200"
              px={3}
              py={1}
              fontSize="sm"
              bg="white"
              _hover={{ borderColor: "gray.300" }}
              _focus={{
                borderColor: "blue.500",
                outline: "none",
                boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
              }}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </Box>
          </HStack>
          <Box display={{ base: "none", md: "flex" }}>
            <HStack gap={4}>
              <ViewToggle value={viewMode} onChange={setViewMode} />
              {hasPermission("students:read") && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExport}
                  disabled={records.length === 0}
                >
                  <HStack gap={1}>
                    <FiDownload />
                    <Text>Export</Text>
                  </HStack>
                </Button>
              )}
              <AddStudent />
            </HStack>
          </Box>
        </Flex>
      </Flex>

      {records.length === 0 ? (
        <SwimEmptyState resource="swimmers" actionTo="/students" />
      ) : (
        <>
          <Box opacity={isPlaceholderData ? 0.5 : 1}>
            {effectiveViewMode === "cards" ? (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
                {records.map((student: StudentResponse) => (
                  <StudentCard
                    key={student.id}
                    student={student}
                    showActions={showActions}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <DataTable columns={columns} data={records} size="sm" />
            )}
          </Box>
          <Flex
            justifyContent={{ base: "center", md: "flex-end" }}
            mt={4}
            gap={2}
          >
            <Button
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled>
              Page {page} of {Math.ceil(count / PER_PAGE)}
            </Button>
            <Button
              size="sm"
              onClick={() =>
                setPage(Math.min(Math.ceil(count / PER_PAGE), page + 1))
              }
              disabled={page >= Math.ceil(count / PER_PAGE)}
            >
              Next
            </Button>
          </Flex>
        </>
      )}
    </Container>
  )
}
